import * as React from "react";

import { cn } from "@/lib/cn";

/**
 * DetailPageLayout
 *
 * Canonical reference: ianfiles/ui-design-system.md §3.1
 *
 * Three-column responsive grid used on every entity detail page:
 *
 *   ┌──────────────────────────────────┐  ┌──────────────────┐
 *   │ MainColumn (lg:col-span-2)       │  │ SideColumn       │
 *   │   <Section> ...                  │  │ <Section> ...    │
 *   └──────────────────────────────────┘  └──────────────────┘
 *
 * On `lg+` it's a two-thirds / one-third split; below `lg` everything stacks
 * into a single column.
 *
 * Children must be `<DetailPageLayout.Main>` and `<DetailPageLayout.Side>`
 * (or `<MainColumn>` / `<SideColumn>`). Either may be omitted — for a
 * single-column detail page (e.g. Sites), pass only `<Main>`.
 */
export function DetailPageLayout({
  children,
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("grid grid-cols-1 lg:grid-cols-3 gap-4", className)}
      {...rest}
    >
      {children}
    </div>
  );
}

function MainColumn({
  children,
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("lg:col-span-2 space-y-4 min-w-0", className)}
      {...rest}
    >
      {children}
    </div>
  );
}
MainColumn.displayName = "DetailPageLayout.Main";

function SideColumn({
  children,
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("space-y-4 min-w-0", className)} {...rest}>
      {children}
    </div>
  );
}
SideColumn.displayName = "DetailPageLayout.Side";

DetailPageLayout.Main = MainColumn;
DetailPageLayout.Side = SideColumn;

export { MainColumn, SideColumn };
